import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthApi, TOKEN_KEY, api } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  const applySession = useCallback((data) => {
    // accepts either a login payload ({token, user, tenant}) or a bare token string
    if (typeof data === "string") {
      localStorage.setItem(TOKEN_KEY, data);
      return AuthApi.me().then((me) => {
        setUser(me?.user || null);
        setTenant(me?.tenant || null);
        return me;
      });
    }
    if (data?.token) localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data?.user || null);
    setTenant(data?.tenant || null);
    return Promise.resolve(data);
  }, []);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      setTenant(null);
      setLoading(false);
      return null;
    }
    try {
      const data = await AuthApi.me();
      setUser(data.user);
      setTenant(data.tenant);
      return data;
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      setTenant(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(
    async (payload) => {
      const data = await AuthApi.login(payload);
      applySession(data);
      return data;
    },
    [applySession]
  );

  const signup = useCallback(
    async (payload) => {
      const data = await AuthApi.signup(payload);
      applySession(data);
      return data;
    },
    [applySession]
  );

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    delete api.defaults.headers.Authorization;
    setUser(null);
    setTenant(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      tenant,
      loading,
      isAdmin: user?.role === "admin",
      // organisation type chosen at signup: "fleet_owner" | "accounting_firm"
      adminSubType: tenant?.admin_sub_type || "fleet_owner",
      // an accounting firm IS the accountant, so "send to accountant" and internal
      // driver payouts make no sense for them (VAT reporting stays visible)
      isAccountingFirm: (tenant?.admin_sub_type || "fleet_owner") === "accounting_firm",
      currency: tenant?.currency || "EUR",
      categories: tenant?.categories || [],
      login,
      signup,
      logout,
      refresh,
      applySession,
      setTenant,
    }),
    [user, tenant, loading, login, signup, logout, refresh, applySession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
